import './story-layout.css';

import type { LandingPageAnalysis } from '@/types/landing-page-analysis';
import type { LandingPageReport } from '@/types/landing-page-analysis';
import type { RestaurantSemData } from '@/types/sem';
import { buildStoryData, getSeverity, calcRevenueLoss } from './story-utils';
import type { SeverityLevel } from './story-utils';
import { normalizeAdGroupName, normalizeRestaurantName } from '@/lib/normalize-names';
import { StoryMasthead } from './StoryMasthead';
import { RevenueHero } from './RevenueHero';
import { StoryTimeline } from './StoryTimeline';
import { AdStep } from './AdStep';
import { LandingStep } from './LandingStep';
import { ImpactStep } from './ImpactStep';
import { FixesStep } from './FixesStep';
import { AnnotatedAudit } from './AnnotatedAudit';
import { CompetitorStep } from './CompetitorStep';
import { AdGroupSection } from './AdGroupSection';

interface LandingPageStoryProps {
  report: LandingPageReport;
  restaurant: RestaurantSemData;
  restaurants: RestaurantSemData[];
}

const SEVERITY_ORDER: Record<SeverityLevel, number> = {
  critical: 0,
  warning: 1,
  ok: 2,
};

/**
 * Build a map from normalized URL -> { adGroupId, adGroupName, campaignId, campaignName }
 * by walking the restaurant's campaign/adGroup/ad tree.
 */
function buildUrlToAdGroupMap(restaurant: RestaurantSemData) {
  const map = new Map<string, { adGroupId: string; adGroupName: string; campaignId: string; campaignName: string }>();
  for (const c of restaurant.data.campaigns) {
    for (const ag of c.adGroups || []) {
      for (const ad of ag.ads || []) {
        if (!ad.finalUrl) continue;
        // Normalize: strip trailing slash, lowercase, remove www
        const norm = ad.finalUrl.replace(/\/$/, '').toLowerCase().replace('://www.', '://');
        map.set(norm, { adGroupId: ag.id, adGroupName: ag.name, campaignId: c.id, campaignName: c.name });
      }
    }
  }
  return map;
}

function normalizeUrl(url: string): string {
  return url.replace(/\/$/, '').toLowerCase().replace('://www.', '://');
}

function groupByAdGroup(
  pages: LandingPageAnalysis[],
  urlMap: Map<string, { adGroupId: string; adGroupName: string; campaignId: string; campaignName: string }>,
  restaurant: RestaurantSemData,
): Map<string, LandingPageAnalysis[]> {
  // Build a name→adGroup lookup for matching pages that already have adGroupName
  const allAdGroups = restaurant.data.campaigns.flatMap(c =>
    (c.adGroups || []).map(ag => ({ ...ag, campaignId: c.id, campaignName: c.name }))
  );

  const groups = new Map<string, LandingPageAnalysis[]>();
  for (const page of pages) {
    let key = page.adGroupId;

    // If page has adGroupName from report, match by name first (more reliable
    // than URL matching when the same URL is used across multiple ad groups)
    if (!key && page.adGroupName) {
      const nameMatch = allAdGroups.find(ag => ag.name === page.adGroupName);
      if (nameMatch) {
        key = nameMatch.id;
        (page as any).adGroupId = nameMatch.id;
        (page as any).campaignId = nameMatch.campaignId;
        (page as any).campaignName = nameMatch.campaignName;
      }
    }

    // Fall back to URL matching
    if (!key) {
      const match = urlMap.get(normalizeUrl(page.url));
      if (match) {
        key = match.adGroupId;
        (page as any).adGroupId = match.adGroupId;
        (page as any).adGroupName = match.adGroupName;
        (page as any).campaignId = match.campaignId;
        (page as any).campaignName = match.campaignName;
      }
    }
    const groupKey = key || '__ungrouped__';
    const arr = groups.get(groupKey) || [];
    arr.push(page);
    groups.set(groupKey, arr);
  }
  return groups;
}

export function LandingPageStory({ report, restaurant, restaurants }: LandingPageStoryProps) {
  // Build URL -> ad group lookup
  const urlMap = buildUrlToAdGroupMap(restaurant);

  // Collect domains unique to this restaurant (exclude shared domains like toasttab.com)
  const allRestaurantDomains = new Map<string, number>();
  for (const r of restaurants) {
    for (const c of r.data.campaigns) {
      for (const ag of c.adGroups || []) {
        for (const ad of ag.ads || []) {
          if (!ad.finalUrl) continue;
          try {
            const d = new URL(ad.finalUrl).hostname.replace('www.', '');
            allRestaurantDomains.set(d, (allRestaurantDomains.get(d) || 0) + 1);
          } catch {}
        }
      }
    }
  }
  const restaurantDomains = new Set<string>();
  for (const [url] of urlMap) {
    try {
      const d = new URL(url).hostname.replace('www.', '');
      // Only use domain matching for domains unique to this restaurant
      if ((allRestaurantDomains.get(d) || 0) <= 1 || d.includes(restaurant.name.toLowerCase().replace(/[^a-z]/g, ''))) {
        restaurantDomains.add(d);
      }
    } catch {}
  }

  // Build campaign name set for matching (report uses "Restaurant - Campaign" prefix format)
  const campaignNames = new Set(restaurant.data.campaigns.map(c => c.name.toLowerCase()));

  const restaurantPagesRaw = report.pages.filter(page => {
    if (page.campaignId && restaurant.data.campaigns.some(c => c.id === page.campaignId)) return true;
    // Match by exact URL
    if (urlMap.has(normalizeUrl(page.url))) return true;
    // Match by campaign name (report format: "RestaurantName - CampaignName")
    if (page.campaignName) {
      const reportName = page.campaignName.toLowerCase();
      // Try stripping "RestaurantName - " prefix → "CampaignName"
      const dashIdx = reportName.indexOf(' - ');
      const stripped = dashIdx >= 0 ? reportName.slice(dashIdx + 3) : reportName;
      if (campaignNames.has(stripped) || campaignNames.has(reportName)) return true;
    }
    // Match by domain (only for non-shared domains)
    try {
      const pageDomain = new URL(page.url).hostname.replace('www.', '');
      return restaurantDomains.has(pageDomain);
    } catch { return false; }
  });

  // Deduplicate by URL — same page can appear multiple times in report.pages
  const seenUrls = new Set<string>();
  const restaurantPages = restaurantPagesRaw.filter(page => {
    const key = normalizeUrl(page.url);
    if (seenUrls.has(key)) return false;
    seenUrls.add(key);
    return true;
  });

  if (restaurantPages.length === 0) {
    return (
      <div className="story-root" style={{ padding: '64px 24px' }}>
        <div className="story-report">
          <p style={{ color: 'var(--t3)', textAlign: 'center' }}>
            No landing pages analyzed for {normalizeRestaurantName(restaurant.name)}.
          </p>
        </div>
      </div>
    );
  }

  // Sort pages by message match (worst first)
  const sortedPages = [...restaurantPages].sort(
    (a, b) => a.scores.messageMatch - b.scores.messageMatch
  );

  // Group by ad group (enriches pages with adGroupId from name/URL matching)
  const groups = groupByAdGroup(sortedPages, urlMap, restaurant);

  // Drop homepage from any ad group that also has a dedicated landing page
  for (const [key, pages] of groups) {
    if (pages.length <= 1) continue;
    const dedicated = pages.filter(p => {
      try { return new URL(p.url).pathname.replace(/\/$/, '').length > 0; }
      catch { return true; }
    });
    if (dedicated.length > 0) groups.set(key, dedicated);
  }

  // Portfolio avg CVR
  const validR = restaurants.filter(r => r.data.summary.clicks > 0);
  const portfolioAvgCvr = validR.length > 0
    ? validR.reduce((s, r) => s + (r.data.summary.conversions / r.data.summary.clicks) * 100, 0) / validR.length
    : 0;

  // Build sorted group entries with severity
  const groupEntries = Array.from(groups.entries()).map(([adGroupId, pages]) => {
    const sum = restaurant.data.summary;
    const pageCvr = sum.clicks > 0 ? (sum.conversions / sum.clicks) * 100 : 0;
    const avgConvValue = sum.conversions > 0 ? sum.conversionValue / sum.conversions : 0;
    const revenueLoss = calcRevenueLoss(portfolioAvgCvr / 100, pageCvr / 100, sum.clicks, avgConvValue);

    let worstSeverity: SeverityLevel = 'ok';
    for (const p of pages) {
      const sev = getSeverity(revenueLoss, p.scores.messageMatch);
      if (SEVERITY_ORDER[sev] < SEVERITY_ORDER[worstSeverity]) worstSeverity = sev;
    }

    // Look up AdGroup from restaurant data
    const adGroup = adGroupId !== '__ungrouped__'
      ? restaurant.data.campaigns.flatMap(c => c.adGroups || []).find(ag => ag.id === adGroupId) || null
      : null;

    const rawName = adGroupId === '__ungrouped__'
      ? 'Other Pages'
      : (adGroup?.name || pages[0]?.adGroupName || 'Ad Group');
    const adGroupName = adGroupId === '__ungrouped__' ? rawName : normalizeAdGroupName(rawName);

    return { adGroupId, adGroupName, adGroup, pages, severity: worstSeverity };
  })
  .sort((a, b) => {
    // Active ad groups first, then paused/zero-spend
    const aActive = a.adGroup ? (a.adGroup.metrics.spend > 0 ? 0 : 1) : 0;
    const bActive = b.adGroup ? (b.adGroup.metrics.spend > 0 ? 0 : 1) : 0;
    if (aActive !== bActive) return aActive - bActive;
    return SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
  });

  return (
    <div className="story-root" style={{ padding: '32px 24px' }}>
      {groupEntries.map(({ adGroupId, adGroupName, adGroup, pages, severity }, idx) => (
        <AdGroupSection
          key={adGroupId}
          adGroupName={adGroupName}
          adGroup={adGroup}
          severity={severity}
          pageCount={pages.length}
          defaultOpen={idx === 0}
        >
          {pages.map((page) => {
            const data = buildStoryData(page, restaurant, restaurants);
            const matchedInsight = restaurant?.aiInsights?.find(
              ins => ins.ad_group_id === page.adGroupId
            );

            return (
              <div key={page.url}>
                <div className="story-report">
                  <StoryMasthead
                    restaurantName={data.restaurantName}
                    campaignName={data.campaignName}
                    domain={data.domain}
                    severity={data.severity}
                  />

                  <RevenueHero
                    revenueLoss={data.revenueLoss}
                    monthlyClicks={data.monthlyClicks}
                    portfolioAvgCvr={data.portfolioAvgCvr}
                    pageCvr={data.pageCvr}
                    avgConvValue={data.avgConvValue}
                  />

                  {matchedInsight && (
                    <div className="mb-4">
                      <span className={`inline-block text-[10px] font-medium px-2 py-1 rounded ${
                        matchedInsight.tier === 'critical'
                          ? 'bg-[var(--ed-red-bg)] text-[var(--ed-red)] border border-[rgba(224,90,82,0.12)]'
                          : 'bg-[var(--ed-amber-bg)] text-[var(--ed-amber)] border border-[rgba(224,168,82,0.12)]'
                      }`}>
                        {matchedInsight.tier === 'critical' ? 'Critical' : 'Warning'} — CVR {matchedInsight.cvr_at_trigger}%
                      </span>
                      {matchedInsight.priority_action && (
                        <div className="mt-2 ed-inset p-3 rounded-lg">
                          <p className="text-xs text-[var(--ed-t3)] uppercase tracking-wider mb-1">Priority Action</p>
                          <p className="text-sm font-medium text-[var(--ed-t1)]">{matchedInsight.priority_action}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {data.pageAudit && (
                    <AnnotatedAudit audit={data.pageAudit} />
                  )}

                  <StoryTimeline>
                    {[
                      <AdStep
                        key="ad"
                        headline={data.adHeadline}
                        ctr={data.ctr}
                        clicks={data.clicks}
                        cpc={data.cpc}
                        keywords={data.topKeywords}
                        status={data.adStatus}
                        insight={matchedInsight}
                      />,
                      <LandingStep
                        key="landing"
                        pageH1={data.pageH1}
                        scores={data.scores}
                        issues={data.issues}
                        status={data.landingStatus}
                        insight={matchedInsight}
                      />,
                      <ImpactStep
                        key="impact"
                        pageCvr={data.pageCvr}
                        portfolioAvgCvr={data.portfolioAvgCvr}
                        qualityScore={data.qualityScore}
                        wastedSpend={data.wastedSpend}
                        clicksLostPct={data.clicksLostPct}
                        insight={matchedInsight}
                      />,
                    ]}
                  </StoryTimeline>

                  <StoryTimeline startIndex={3}>
                    {[
                      <FixesStep
                        key="fixes"
                        recommendations={data.recommendations}
                        revenueLoss={data.revenueLoss}
                        projectedCvr={data.projectedCvr}
                        pageCvr={data.pageCvr}
                        projectedQualityScore={data.projectedQualityScore}
                        qualityScore={data.qualityScore}
                      />,
                      ...(data.competitorAudit ? [
                        <CompetitorStep
                          key="competitor"
                          audit={data.competitorAudit}
                          pageAudit={data.pageAudit}
                        />,
                      ] : []),
                    ]}
                  </StoryTimeline>

                  <div className="story-ft story-anim-4">
                    All figures from Google Ads account · Revenue ={' '}
                    <code>(avg CVR − page CVR) × clicks × conv value</code>
                    {data.pageAudit && (() => {
                      const h = Math.round((Date.now() - new Date(data.pageAudit.analyzed_at).getTime()) / (1000 * 60 * 60));
                      return ` · Synced ${h}h ago`;
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </AdGroupSection>
      ))}
    </div>
  );
}
